import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import { ROLE_COPY, TENANCY_COPY } from '../../constants/partnerCopy';
import companyAdminService from '../../services/companyAdminService';
import CompanyUsersPanel from './CompanyUsersPanel';
import CompanyRolesPanel from './CompanyRolesPanel';
import CompanyEntitlementsPanel from './CompanyEntitlementsPanel';
import CompanyLLMProvidersPanel from './CompanyLLMProvidersPanel';
import CompanyResourcesPanel from './CompanyResourcesPanel';
import UsageAnalyticsPanel from '../shared/UsageAnalytics/UsageAnalyticsPanel';
import PlatformMcpActivityTable from '../shared/PlatformMcpActivity/PlatformMcpActivityTable';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

/**
 * Company Admin console (spec §5). Tabs are gated per-permission:
 * Users needs user_management, Roles needs role_management; the read-only
 * Entitlements view is always available inside the console. Everything is scoped
 * to the admin's own company by the backend.
 */
const CompanyAdmin = () => {
  const { isDarkMode } = useTheme();
  const { has, hasAny, company, partner, isPartnerStaff, ready } = usePermissions();
  const [catalog, setCatalog] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Stable identity: panels use `notify` in useCallback/useEffect deps, so an
  // unstable notify would re-trigger their data-loaders on every toast/re-render.
  const notify = useCallback(
    (message, severity = 'info') => setSnackbar({ open: true, message, severity }),
    []
  );

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  // True when the user holds at least one actual admin key. resource_sharing is
  // NOT an admin key — alone it unlocks only the Company Resources view, and the
  // header should not present the screen as an admin console (spec §4).
  const isAdminConsole =
    ready &&
    (isPartnerStaff
      ? has(PERMISSIONS.ROLE_MANAGEMENT)
      : hasAny([
          PERMISSIONS.USER_MANAGEMENT,
          PERMISSIONS.ROLE_MANAGEMENT,
          PERMISSIONS.LLM_KEYS_COMPANY,
          PERMISSIONS.USAGE_ANALYTICS,
        ]));

  const tabs = useMemo(() => {
    if (!ready) return [];
    const list = [];
    // Partner staff (Partner tier guide §3.2): users, keys, limits and usage live
    // in the Partner area; the one thing left here is custom roles for direct
    // users (plus browsing the home company's resources).
    if (isPartnerStaff) {
      if (has(PERMISSIONS.ROLE_MANAGEMENT)) list.push({ value: 'roles', label: 'Roles' });
      if (has(PERMISSIONS.RESOURCE_SHARING)) list.push({ value: 'resources', label: 'Resources' });
      return list;
    }
    if (has(PERMISSIONS.USER_MANAGEMENT)) list.push({ value: 'users', label: 'Users' });
    if (has(PERMISSIONS.ROLE_MANAGEMENT)) {
      list.push({ value: 'roles', label: 'Roles' });
      // The Entitlements view reads /company-admin/entitlements and the
      // permission catalog — both gated by role_management (spec §3), so showing
      // the tab to any other admin would only produce 403s.
      list.push({ value: 'entitlements', label: 'Entitlements' });
    }
    if (has(PERMISSIONS.LLM_KEYS_COMPANY)) list.push({ value: 'llmProviders', label: 'LLM Providers' });
    if (has(PERMISSIONS.USAGE_ANALYTICS)) {
      list.push({ value: 'usage', label: 'LLM Usage' });
      list.push({ value: 'mcpActivity', label: PLATFORM_MCP_COPY.activityAdminMenu });
    }
    if (has(PERMISSIONS.RESOURCE_SHARING)) list.push({ value: 'resources', label: 'Company Resources' });
    return list;
  }, [has, ready, isPartnerStaff]);

  // Never print the home company's backend name (`partner-{id}-home`).
  const orgName = isPartnerStaff ? partner?.name : company?.name;
  let title = isAdminConsole ? 'Company Admin' : 'Company Resources';
  let subtitle;
  if (isPartnerStaff) {
    title = isAdminConsole ? ROLE_COPY.directUsersRoles : 'Resources';
    subtitle = isAdminConsole
      ? `Create custom roles for ${orgName || 'your partner'}'s direct users.`
      : `Browse and share resources created by ${orgName || 'your partner'}'s direct users.`;
  } else if (isAdminConsole) {
    subtitle = orgName ? `Manage users and roles for ${orgName}.` : 'Manage users and roles for your company.';
  } else {
    subtitle = orgName
      ? `Browse and share resources created by anyone in ${orgName}.`
      : 'Browse and share resources created by anyone in your company.';
  }
  const managedBy = !isPartnerStaff && partner?.name ? TENANCY_COPY.managedBy(partner.name) : null;

  const [tab, setTab] = useState('users');

  // Derive the rendered tab from the permitted set so we never render a panel the
  // user can't use (which would 403), even for the render before the effect runs.
  const activeTab = tabs.some((t) => t.value === tab) ? tab : tabs[0]?.value;

  // Keep the stored tab in sync once permissions resolve.
  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.value === tab)) {
      setTab(tabs[0].value);
    }
  }, [tabs, tab]);

  // Permission catalog (envelope-filtered) — shared by the Roles builder and the
  // read-only Entitlements view.
  useEffect(() => {
    if (!ready || !has(PERMISSIONS.ROLE_MANAGEMENT)) {
      setCatalog([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await companyAdminService.getPermissions();
        const list = Array.isArray(data?.permissions)
          ? data.permissions
          : Array.isArray(data)
          ? data
          : [];
        if (!cancelled) setCatalog(list);
      } catch (err) {
        if (!cancelled) notify(err?.message || 'Failed to load permission catalog.', 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify, ready, has]);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.layout}>
        <Box className={styles.inner}>
          <Box className={styles.header}>
            <Box className={styles.titleSection}>
              <Typography variant="h4" component="h1">
                {title}
              </Typography>
              <Typography variant="body2">
                {subtitle}
                {managedBy && ` ${managedBy}.`}
              </Typography>
            </Box>
          </Box>

          {ready && !company ? (
            <Box className={styles.noteBanner}>
              <span>
                You aren't a member of a company, so there's nothing to administer here. This console is
                for company administrators.
              </span>
            </Box>
          ) : (
            <>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setTab(v)}
                className={styles.consoleTabs}
                TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {tabs.map((t) => (
                  <Tab key={t.value} value={t.value} label={t.label} className={styles.tab} />
                ))}
              </Tabs>

              {activeTab === 'users' && <CompanyUsersPanel notify={notify} />}
              {activeTab === 'roles' && <CompanyRolesPanel notify={notify} catalog={catalog} />}
              {activeTab === 'entitlements' && <CompanyEntitlementsPanel notify={notify} catalog={catalog} />}
              {activeTab === 'llmProviders' && <CompanyLLMProvidersPanel notify={notify} />}
              {activeTab === 'usage' && (
                <UsageAnalyticsPanel basePath="/company-admin/usage" scope="company" notify={notify} />
              )}
              {activeTab === 'mcpActivity' && <PlatformMcpActivityTable scope="company" />}
              {activeTab === 'resources' && <CompanyResourcesPanel notify={notify} />}
            </>
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default CompanyAdmin;
